import React from 'react';
import { Helmet } from 'react-helmet';
import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
// The intake stylesheet owns the palette and type tokens under `.intake`, and this page is the same
// document at a later moment, so it wears the same scope rather than redeclaring them. Importing it
// here is not a duplicate: webpack resolves it to the module IntakeView already pulled in.
import '../IntakeView/IntakeView.scss';
import './IntakeReturnView.scss';

/**
 * Where Stripe sends a client after they pay (register item 35).
 *
 * Until now these two paths did not exist. `/public/intake/complete` has always redirected to
 * `${INTAKE_URL}/thank-you` or `/received`, and with INTAKE_URL unset in production both resolved to
 * texasticketdoctor.com, where the router's `path: "*"` answered them with NotFoundView. A client
 * paid $375 and landed on Not Found. Found by running the intake end to end on Aug 14 2026.
 *
 * Two outcomes, one page, because they differ by two paragraphs and must never drift apart:
 *
 *  - `paid` — the payment succeeded and the case exists.
 *  - `received` — the payment succeeded and the case did NOT get created. The client must not be able
 *    to tell the difference in tone. Their money is fine either way, and that is the only thing they
 *    can act on; the failure is the firm's problem and staff already have the alert.
 *
 * Deliberately carries NO personal data — no name, no citation, no amount, and nothing in the URL or
 * the page title. That is the commitment made to Lloyd and Ken about analytics on this subdomain, and
 * this is the one page in the flow a client reaches immediately after typing everything they have.
 * It also means the page needs no props from the redirect, which is why the redirect has none.
 */

const OFFICE_PHONE = '210-210-1616';

type Outcome = 'paid' | 'received';

interface IntakeReturnViewProps {
  outcome: Outcome;
}

/** What differs between the two outcomes. Everything else on the page is shared. */
const COPY: Record<
  Outcome,
  { title: string; docTitle: string; steps: string[] }
> = {
  paid: {
    title: 'Your request is with our office',
    docTitle: 'Payment received',
    steps: [
      'We read the citation details you gave us and check them against the court you chose.',
      'Someone from the office contacts you to confirm those details and tell you anything we still need.',
      'We take it from there with the court.',
    ],
  },
  received: {
    title: 'We have your payment and your answers',
    docTitle: 'Payment received — being completed',
    steps: [
      'Our office was told the moment this happened, automatically. Nobody here has to notice it first.',
      'Someone will call you to confirm your details and finish setting the case up.',
      // Not repeating the number here: it is already in the bar above and the line below, and three
      // printings of the same eleven digits reads as a page that is worried.
      'If you would rather not wait, call us and say you paid online.',
    ],
  },
};

const IntakeReturnView = ({ outcome }: IntakeReturnViewProps) => {
  const copy = COPY[outcome];

  return (
    <div className="intake intake-return">
      <Helmet>
        <title>{copy.docTitle}</title>
      </Helmet>

      <header className="intake__bar">
        <span className="intake__mark">Texas Ticket Doctor</span>
        <a className="intake__phone" href={`tel:${OFFICE_PHONE}`}>
          {OFFICE_PHONE}
        </a>
      </header>

      <main className="intake__sheet">
        <div className="intake__masthead intake-return__masthead">
          <p className="intake__eyebrow">Receipt</p>
          <h1 className="intake__title">{copy.title}</h1>
          {/* The signature of the page, and a continuation of the tier stamp on the intake form
              rather than a new device: the document has been stamped and handed back. */}
          <span className="intake-return__stamp" aria-hidden="true">
            Paid
          </span>
        </div>

        <section className="intake__fee intake__fee--resolved">
          <p className="intake__field-label">Payment</p>
          <p className="intake-return__status">Received</p>
          <p className="intake__amount-note">
            Your card was charged once, for our fee. Court costs are separate and are not part of
            this payment — we will tell you what they are before anything is due.
          </p>
        </section>

        {/* `received` only: the client has paid and no case exists yet. The one thing they could get
            wrong from here is paying a second time, so it is stated on its own, in ink rather than
            the red used elsewhere for problems. Red beside a payment reads as "your money failed",
            which is the opposite of what happened. */}
        {outcome === 'received' && (
          <section className="intake-return__hold">
            <p className="intake-return__hold-line">Do not pay again.</p>
            <p className="intake-return__hold-note">
              Setting your case up needs a person here rather than the website, and that is already
              in hand. Nothing you filled in has been lost.
            </p>
          </section>
        )}

        {/* Numbered because this genuinely is a sequence in time, not for decoration. */}
        <section className="intake-return__next">
          <p className="intake__field-label">What happens next</p>
          <ol className="intake-return__steps">
            {copy.steps.map((step, index) => (
              <li key={step}>
                <span className="intake-return__step-number" aria-hidden="true">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="intake-return__keep">
          <p>
            If the court sends you anything — a notice, a new date, a letter — tell us. Hiring us
            does not mean you can ignore post from the court.
          </p>
        </section>

        <p className="intake__legal">
          Anything you want to ask before you hear from us? Call{' '}
          <a href={`tel:${OFFICE_PHONE}`}>{OFFICE_PHONE}</a>.
        </p>
      </main>
    </div>
  );
};

export default IntakeReturnView;
